/**
 * FilterSection Component - Individual Filter Section
 * 
 * Atomic design: Molecule-level component for filter categories, difficulties, or time ranges.
 * Extracted from FilterPanel.tsx to maintain <300 line limit per PROJECT_RULES.md.
 * 
 * Responsibilities:
 * - Render individual filter section with checkboxes
 * - Handle option selection/deselection
 * - Display result counts if provided
 * - Maintain accessibility with ARIA labels
 */

import React, { useCallback } from 'react';
import { Checkbox } from './design-system';

export interface FilterOption {
  id: string;
  label: string;
  count?: number; // For showing result counts per filter
}

interface FilterSectionProps {
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  showCounts?: boolean;
  ariaLabel: string;
}

export const FilterSection: React.FC<FilterSectionProps> = React.memo(({
  title,
  options,
  selectedValues,
  onSelectionChange,
  showCounts = false,
  ariaLabel,
}) => {
  // Handle individual option toggle
  const handleOptionToggle = useCallback((optionId: string) => {
    const newValues = selectedValues.includes(optionId)
      ? selectedValues.filter(id => id !== optionId)
      : [...selectedValues, optionId];
    onSelectionChange(newValues);
  }, [selectedValues, onSelectionChange]);

  return (
    <div className="filter-section" role="group" aria-label={ariaLabel}>
      <h3 className="text-sm font-medium text-primary mb-3">{title}</h3>
      <div className="space-y-2">
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.id);
          const countText = showCounts && option.count !== undefined 
            ? `${option.count} results` 
            : undefined;
          
          return (
            <div key={option.id} className="flex items-center hover:bg-surface-secondary p-2 rounded-md transition-colors">
              <Checkbox
                checked={isSelected}
                onChange={() => handleOptionToggle(option.id)}
                label={option.label}
                helperText={countText}
                size="sm"
                aria-describedby={showCounts && option.count ? `${option.id}-count` : undefined}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

FilterSection.displayName = 'FilterSection';

export default FilterSection;